import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { OrganizationType } from '@wonderschool/common-base-types';
import { useFilesAccess } from '@wonderschool/file-service-client';

import { ArrowLeftIcon, ArrowRightIcon, Button } from '@wonderschool/common-base-ui';

import { useOrganization, useOrganizationContactsListener } from '../../../hooks/useOrganizations';
import { logError } from '../../../rollbar';

import { useContacts } from '../../../contacts/contactsHooks';
import { RouteNameEnum, useRoutes } from '../../../navigation';
import { useStudent } from '../../../students/studentsHooks';
import { getScheduledDaysAbbreviated } from '../../enrollmentsUtils';

import LoadingComponent from '../../../Components/Shared/LoadingComponent';
import PageHeader from '../../../Components/Shared/PageHeader';
import { showErrorToast } from '../../../Components/Shared/showToast';

import { saveStudentAndContacts } from '../../enrollmentsAPI';
import { useDocuments, useScheduledDays } from '../../enrollmentsHooks';
import { createInvitationRecipientsFromFamily } from '../../enrollmentsUtils';
import { DocumentType, InvitationRecipientType, OnSaveDataType, OnSaveParamsType } from '../../types';
import { DocumentsForm, InvitationRecipientsForm, ScheduleForm } from '../forms';

type EnrollmentPageProps = {
  onClick?: () => void;
};

const EnrollmentPage: React.FC<EnrollmentPageProps> = () => {
  const { t } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);
  const [isWritingData, setIsWritingData] = useState(false);
  const [dataToSave, setDataToSave] = useState<OnSaveDataType>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const params = useParams<{ studentId: string }>();
  const student = useStudent(params.studentId!);
  const { familyContacts, contacts: _contacts } = useContacts();

  const { goBack, gotoRouteByName } = useRoutes();

  const organization: OrganizationType = useOrganization();
  useOrganizationContactsListener(organization?.id);

  const documents = useDocuments(student);
  const scheduledDays = useScheduledDays(student);

  const invitationRecipients = useMemo<InvitationRecipientType[]>(() => {
    return student ? createInvitationRecipientsFromFamily(student) : [];
  }, [student]);

  const {
    shareFiles,
    data: _shareData,
    isLoading: _isSharing,
    error: shareError,
    isError: isError,
    isSuccess: isSuccess,
  } = useFilesAccess();

  const shouldSaveToFirestore = useCallback(() => {
    return isSaving && Object.keys(dataToSave).length >= 3 && Object.keys(errors).length === 0 && !isWritingData;
  }, [isSaving, dataToSave, errors, isWritingData]);

  // If the user entered an email address, add it to the contact and save
  const contactsToSaveToFirestore = useMemo(() => {
    if (!familyContacts || !dataToSave.invitationRecipients) return [];

    const recipients = (dataToSave.invitationRecipients ?? []) as InvitationRecipientType[];
    const changedContacts: any[] = [];

    for (const contact of familyContacts) {
      const recipient = recipients.find((r) => r.contactId === contact.id && r.email !== contact.email);

      if (recipient) {
        changedContacts.push({ id: contact.id, email: recipient.email });
      }
    }
    return changedContacts;
  }, [dataToSave.invitationRecipients, familyContacts]);

  const studentToSaveToFirestore = useMemo(() => {
    const data: any = {
      id: student?.id ?? '',
      enrollment: {
        invitationRecipients: dataToSave.invitationRecipients ?? [],
        documents: dataToSave.documents ?? [],
        tuitionAndFees: dataToSave.tuitionAndFees ?? {},
        scheduledDays: dataToSave.scheduledDays ?? [],
      },
    };
    // This is legacy and should be deprecated
    data.schedule = getScheduledDaysAbbreviated(data);
    return data;
  }, [dataToSave, student?.id]);

  const shareDocuments = useCallback(() => {
    const documents = dataToSave.documents as DocumentType[] | undefined;
    const documentIds = documents?.map((doc: DocumentType) => doc.id) ?? [];
    // The isError effect below owns the director-facing message. This catch exists so
    // the rejection is not an unhandled promise, and it logs rather than discards, so
    // anyone impersonating the account to investigate still has something to read.
    shareFiles({
      files: documentIds,
      students: [studentToSaveToFirestore.id],
    }).catch((error) => {
      console.error('Enrollment documents - shareFiles rejected', error);
      logError('Enrollment documents - shareFiles rejected', error);
    });
  }, [dataToSave.documents, shareFiles, studentToSaveToFirestore.id]);

  const onSave = useCallback(
    ({ data, errors: errorsParam }: OnSaveParamsType, fieldName: string) => {
      if (!isSaving) return false;
      if (errorsParam && Object.keys(errorsParam).length > 0) {
        setIsSaving(false);
        setErrors((prev) => ({ ...prev, ...errorsParam }));
        setDataToSave({});
      } else {
        setDataToSave((prev) => ({ ...prev, [fieldName]: data }));
      }
    },
    [isSaving]
  );

  useEffect(() => {
    const saveLocal = async () => {
      try {
        setIsWritingData(true);
        await saveStudentAndContacts({
          organization,
          student: studentToSaveToFirestore,
          contacts: contactsToSaveToFirestore,
        });

        const documents = dataToSave.documents as DocumentType[] | undefined;
        if (documents && documents.length > 0) {
          shareDocuments();
        }
      } catch (error) {
        setIsWritingData(false);
        setIsSaving(false);
        setErrors({ save: `Error while saving student or sharing documents: ${error}` });
        logError('Error while saving student or sharing documents', error);
      }
    };
    if (shouldSaveToFirestore()) {
      saveLocal();
    }
  }, [
    contactsToSaveToFirestore,
    dataToSave.documents,
    organization,
    shareDocuments,
    shouldSaveToFirestore,
    studentToSaveToFirestore,
  ]);

  // A failed share used to advance the director exactly like a successful one
  // (CCMS-8163). The grant is the only thing that lets the family read the packet,
  // so moving on from a failure hands them an enrollment whose documents they
  // cannot open, with nothing on screen to say so.
  //
  // `reportedShareError` keeps one failure to one toast and one rollbar entry. The
  // effect also depends on `t`, so without it a language change - or any future churn
  // in the identity of the error react-query hands back - would re-report a failure
  // the director has already been told about.
  const reportedShareError = useRef<unknown>(null);
  useEffect(() => {
    if (!isError) return;
    if (reportedShareError.current === shareError) return;
    reportedShareError.current = shareError;

    setIsWritingData(false);
    setIsSaving(false);
    logError('Enrollment documents - sharing failed', shareError);
    showErrorToast(t('enrollments.documentSharingErrorTitle'), t('enrollments.documentSharingFailed'));
  }, [isError, shareError, t]);

  // Redirect to summary page when document sharing has completed
  useEffect(() => {
    const documents = dataToSave.documents as DocumentType[] | undefined;
    if (isSuccess || (isSaving && documents?.length == 0)) {
      setIsWritingData(false);
      if (organization.stripe?.accountId) {
        gotoRouteByName(RouteNameEnum.STUDENT_ENROLLMENT_INVOICE, [
          { name: 'studentId', value: studentToSaveToFirestore.id },
        ]);
      } else {
        gotoRouteByName(RouteNameEnum.STUDENT_ENROLLMENT_SUMMARY, [
          { name: 'studentId', value: studentToSaveToFirestore.id },
        ]);
      }
    }
  }, [
    isSaving,
    dataToSave.documents,
    gotoRouteByName,
    isSuccess,
    studentToSaveToFirestore.id,
    organization.stripe?.accountId,
  ]);

  if (!student) return <LoadingComponent />;

  return (
    <div className="p-6">
      <form className={`space-y-8 ${isSaving ? 'opacity-50' : ''}`}>
        <PageHeader pageName={'Enrollment'} classes="enrollments-page" />
        <h1 className="text-3xl font-bold">{t('enrollments.pageTitle', { name: student.displayName ?? 'N/A' })}</h1>
        <div className="overflow-hidden bg-white p-6 shadow-md sm:rounded-lg">
          <InvitationRecipientsForm
            invitationRecipients={invitationRecipients}
            isSaving={isSaving && !dataToSave.invitationRecipients}
            onSave={(d) => onSave(d, 'invitationRecipients')}
          />
        </div>
        <div className="overflow-hidden bg-white p-6 shadow-md sm:rounded-lg">
          <DocumentsForm
            documents={documents}
            isSaving={isSaving && !dataToSave.documents}
            onSave={(d) => onSave(d, 'documents')}
          />
        </div>
        <div className="bg-white p-6 shadow-md sm:rounded-lg">
          <ScheduleForm
            scheduledDays={scheduledDays}
            isSaving={isSaving && !dataToSave.scheduledDays}
            onSave={(d) => onSave(d, 'scheduledDays')}
          />
        </div>
        <CommandButtons />
      </form>
    </div>
  );
  function CommandButtons() {
    return (
      <div className="flex justify-between space-x-4" data-testid="ws-form-buttons">
        <Button
          onClick={() => {
            setDataToSave({});
            goBack();
          }}
          data-testid="ws-previou-btn"
          preIcon={<ArrowLeftIcon className="size-6" />}
        >
          {t('common.previous')}
        </Button>
        <Button
          primary
          loading={isSaving}
          disabled={isSaving}
          onClick={() => {
            setIsSaving(true);
            setErrors({});
          }}
          data-testid="ws-finish-btn"
          postIcon={<ArrowRightIcon className="size-6" />}
        >
          {t('common.continue')}
        </Button>
      </div>
    );
  }
};

export default EnrollmentPage;
